import { useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { authAPI } from '../api/endpoints';

const ROLE_LABELS = { admin: 'Administrador', operator: 'Operador', viewer: 'Solo Dashboard' };
const ROLE_COLORS = { admin: 'blue', operator: 'purple', viewer: 'default' };

const UsersPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const { data, refetch } = useQuery('users', () => authAPI.listUsers());
  const users = data?.data || [];

  const createMutation = useMutation(authAPI.createUser);
  const updateMutation = useMutation(({ id, data: payload }) => authAPI.updateUser(id, payload));
  const deleteMutation = useMutation(authAPI.deleteUser);

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'viewer' });
    setIsModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({ role: user.role });
    setIsModalOpen(true);
  };

  const handleDelete = (user) => {
    Modal.confirm({
      title: `Eliminar usuario "${user.username}"`,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(user.id);
          message.success('Usuario eliminado');
          refetch();
        } catch {
          message.error('Error al eliminar el usuario');
        }
      },
    });
  };

  const onFinish = async (values) => {
    try {
      if (editingUser) {
        await updateMutation.mutateAsync({ id: editingUser.id, data: { role: values.role } });
        message.success('Rol actualizado correctamente');
      } else {
        await createMutation.mutateAsync(values);
        message.success('Usuario creado correctamente');
      }
      setIsModalOpen(false);
      form.resetFields();
      refetch();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al guardar el usuario';
      message.error(msg);
    }
  };

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Nombre',
      key: 'name',
      render: (_, r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || '—',
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>
          {ROLE_LABELS[role] || role}
        </Tag>
      ),
    },
    {
      title: 'Activo',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Sí' : 'No'}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Cambiar rol
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={<span><TeamOutlined style={{ marginRight: 8 }} />Gestión de Usuarios</span>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo Usuario
          </Button>
        }
      >
        <Table dataSource={users} columns={columns} rowKey="id" />
      </Card>

      <Modal
        title={editingUser ? `Cambiar rol — ${editingUser.username}` : 'Nuevo Usuario'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {!editingUser && (
            <>
              <Form.Item
                name="username"
                label="Usuario"
                rules={[{ required: true, message: 'Campo requerido' }]}
              >
                <Input autoComplete="off" />
              </Form.Item>
              <Form.Item name="first_name" label="Nombre">
                <Input />
              </Form.Item>
              <Form.Item name="last_name" label="Apellido">
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email">
                <Input type="email" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Contraseña"
                rules={[
                  { required: true, message: 'Campo requerido' },
                  { min: 8, message: 'Mínimo 8 caracteres' },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="role"
            label="Rol"
            rules={[{ required: true, message: 'Selecciona un rol' }]}
          >
            <Select>
              <Select.Option value="admin">
                Administrador — acceso completo
              </Select.Option>
              <Select.Option value="operator">
                Operador — clientes, facturas, pagos y cartera
              </Select.Option>
              <Select.Option value="viewer">
                Solo Dashboard — solo lectura del dashboard
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;
